import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/ListPage.css';

function VendorList() {
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(true);
    const [vendors, setVendors] = useState([]);
    const [selectedVendors, setSelectedVendors] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredVendors, setFilteredVendors] = useState([]);

    useEffect(() => {
        withLoading(async () => {
            const res = await api.get('/vendors');
            setVendors(res.data);
            setFilteredVendors(res.data);
        }).catch((err) => console.error('Error fetching vendors:', err));
    }, [withLoading]);

    useEffect(() => {
        const term = searchTerm.toLowerCase();
        const results = vendors.filter(vendor =>
            (vendor.name || '').toLowerCase().includes(term) ||
            (vendor.vendorType || '').toLowerCase().includes(term) ||
            (vendor.projectName || '').toLowerCase().includes(term) ||
            (vendor.mobileNumber || '').toLowerCase().includes(term) ||
            (vendor.email || '').toLowerCase().includes(term) ||
            (vendor.address || '').toLowerCase().includes(term)
        );
        setFilteredVendors(results);
    }, [searchTerm, vendors]);

    const handleCheckboxChange = (vendorId) => {
        setSelectedVendors(prev =>
            prev.includes(vendorId) ? prev.filter(id => id !== vendorId) : [...prev, vendorId]
        );
    };

    const handleSelectAll = (e) => {
        setSelectedVendors(e.target.checked ? filteredVendors.map(vendor => vendor.id) : []);
    };

    const handleDeleteSelected = () => {
        if (!window.confirm('Are you sure you want to delete the selected vendors?')) return;
        Promise.all(selectedVendors.map(vendorId => api.delete(`/vendors/${vendorId}`)))
            .then(() => {
                setVendors(prev => prev.filter(vendor => !selectedVendors.includes(vendor.id)));
                setSelectedVendors([]);
            })
            .catch(() => {
                alert('Error deleting vendors');
            });
    };

    const allSelected = filteredVendors.length > 0 && selectedVendors.length === filteredVendors.length;

    return (
        <PageLoadingArea isLoading={isLoading}>
        <div className="list-page">
            <div className="list-page-header">
                <div className="list-page-title">
                    <h1>Vendors</h1>
                    <p className="list-page-subtitle">
                        {filteredVendors.length} vendor{filteredVendors.length === 1 ? '' : 's'}
                    </p>
                </div>
                <div className="list-header-actions">
                    <div className="list-search-wrap">
                        <input
                            type="text"
                            placeholder="Search vendor, type, or project"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="list-search-input"
                        />
                    </div>
                    <div className="list-button-group">
                        <button
                            type="button"
                            onClick={() => navigate('/admin')}
                            className="list-secondary-button"
                        >
                            Back to admin
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/admin/vendor-types')}
                            className="list-secondary-button"
                        >
                            Vendor Types
                        </button>
                        <button type="button" onClick={() => navigate('/createVendor')} className="list-add-button">
                            Add New Vendor
                        </button>
                        {selectedVendors.length > 0 && (
                            <button type="button" onClick={handleDeleteSelected} className="list-delete-button">
                                Delete ({selectedVendors.length})
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="list-table-card">
                <div className="list-table-scroll">
                    <table className="list-table">
                        <thead>
                            <tr>
                                <th className="col-check">
                                    <input type="checkbox" checked={allSelected} onChange={handleSelectAll} aria-label="Select all" />
                                </th>
                                <th>Sr No</th>
                                <th>Name</th>
                                <th>Vendor Type</th>
                                <th>Mobile</th>
                                <th>Email</th>
                                <th>Project</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVendors.length === 0 ? (
                                <tr className="list-empty-row">
                                    <td colSpan="7">
                                        <div className="list-empty-state">
                                            <h3>No vendors found</h3>
                                            <p>{searchTerm ? 'Try a different search term.' : 'Add your first vendor to get started.'}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredVendors.map((vendor, index) => (
                                <tr
                                    key={vendor.id}
                                    className={`list-row${selectedVendors.includes(vendor.id) ? ' is-selected' : ''}`}
                                    onClick={(e) => {
                                        if (e.target.type !== 'checkbox') navigate(`/createVendor/${vendor.id}`);
                                    }}
                                >
                                    <td className="col-check" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedVendors.includes(vendor.id)}
                                            onChange={() => handleCheckboxChange(vendor.id)}
                                        />
                                    </td>
                                    <td>{index + 1}</td>
                                    <td><span className="list-primary-text">{vendor.name}</span></td>
                                    <td>{vendor.vendorType}</td>
                                    <td>{vendor.mobileNumber}</td>
                                    <td>{vendor.email || '—'}</td>
                                    <td><span className="list-muted-text">{vendor.projectName || '—'}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        </PageLoadingArea>
    );
}

export default VendorList;
